/**
 * Ajustes predeterminados y su normalización (design.md, 7.3).
 *
 * Código puro: lo usan schema.ts (al crear los ajustes) y backup-format.ts
 * (al restaurar), sin que el formato de respaldo dependa de Dexie.
 */

import { esFechaLocal } from '@/domain/dates';
import { DISCO_MAXIMO } from '@/domain/discos';
import {
  NIVELES_ACTIVIDAD,
  VERSION_ESQUEMA,
  type CifradoRespaldo,
  type ComidaHabitual,
  type PeriodoMetasManuales,
  type Settings,
  type TipoMedida,
} from './types';

/**
 * Valores predeterminados.
 *
 * Objeto congelado y sin marca de tiempo a propósito: se devuelve en las
 * lecturas cuando todavía no existe el registro y debe ser siempre el mismo
 * valor, para que una consulta reactiva no crea que cambió.
 *
 * `perfilId` va vacío: el identificador real se crea una sola vez en
 * asegurarSettings(), al arrancar (Req. 4.7).
 */
export const SETTINGS_PREDETERMINADOS: Readonly<Settings> = Object.freeze({
  id: 1 as const,
  schemaVersion: VERSION_ESQUEMA,
  perfilId: '',
  nombre: null,
  sexo: null,
  fechaNacimiento: null,
  estaturaCm: null,
  estaturaCapturada: null,
  nivelActividad: null,
  primerosPasosHechos: false,
  unidadPeso: 'kg' as const,
  unidadLongitud: 'cm' as const,
  tema: 'sistema' as const,
  descansoPredeterminadoSeg: 90,
  incrementoPredeterminadoKg: 2.5,
  pantallaEncendida: true,
  avisoConPantallaBloqueada: false,
  avisoConAppCerrada: false,
  rutinasDe: null,
  modalidadGym: 'gimnasio' as const,
  barraYDiscos: null,
  basculaTendenciaId: null,
  ultimaBasculaId: null,
  medidasOcultas: Object.freeze([]) as unknown as Settings['medidasOcultas'],
  metodoAyuno: 'comidas' as const,
  metaKcalManual: null,
  metaProteinaManualG: null,
  metaCarbohidratosManualG: null,
  metaGrasaManualG: null,
  metaAyunoManualHoras: null,
  metasManualesDesde: null,
  metasManualesAnteriores: Object.freeze([]) as unknown as PeriodoMetasManuales[],
  favoritosBase: Object.freeze([]) as unknown as Settings['favoritosBase'],
  comidasHabituales: Object.freeze([]) as unknown as ComidaHabitual[],
  avisoEtapaVisto: null,
  avisoAyunoLargoVisto: null,
  ventanaCerradaEn: null,
  ultimoRespaldoEn: null,
  diasAvisoRespaldo: 7,
  almacenamientoPersistente: null,
  cifradoRespaldo: null,
  creadoEn: '',
});

const TIPOS_MEDIDA: readonly TipoMedida[] = [
  'cintura',
  'cadera',
  'pecho',
  'brazo',
  'muslo',
  'pantorrilla',
  'cuello',
  'grasa',
];

/** Tope de iteraciones de un verificador leído de un archivo. */
const ITERACIONES_MAXIMAS = 10_000_000;

const esNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const esTextoONulo = (v: unknown) => v === null || typeof v === 'string';
const numeroONulo = (v: unknown) => v === null || (esNumero(v) && v >= 0);
const unoDe =
  <T>(opciones: readonly T[], nulo = false) =>
  (v: unknown) =>
    (nulo && v === null) || opciones.includes(v as T);

/** La barra y los discos de la calculadora (Req. 26.4; 0.8.1), con su unidad. */
function barraYDiscosValido(v: unknown): boolean {
  if (v === null) return true;
  if (typeof v !== 'object' || Array.isArray(v)) return false;
  const c = v as Record<string, unknown>;
  const discos = c['discos'];
  return (
    unoDe(['kg', 'lb'])(c['unidad']) &&
    esNumero(c['barra']) &&
    (c['barra'] as number) >= 0 &&
    (c['barra'] as number) <= DISCO_MAXIMO * 2 &&
    Array.isArray(discos) &&
    discos.length > 0 &&
    discos.every((d) => esNumero(d) && d > 0 && d <= DISCO_MAXIMO)
  );
}

function cifradoValido(v: unknown): v is CifradoRespaldo | null {
  if (v === null) return true;
  if (typeof v !== 'object' || Array.isArray(v)) return false;
  const c = v as Record<string, unknown>;
  return (
    typeof c['sal'] === 'string' &&
    typeof c['verificador'] === 'string' &&
    typeof c['activadoEn'] === 'string' &&
    Number.isInteger(c['iteraciones']) &&
    (c['iteraciones'] as number) >= 1 &&
    (c['iteraciones'] as number) <= ITERACIONES_MAXIMAS
  );
}

function periodoValido(v: unknown): v is PeriodoMetasManuales {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const p = v as Record<string, unknown>;
  return (
    (p['desde'] === null || esFechaLocal(p['desde'])) &&
    esFechaLocal(p['hasta']) &&
    (p['desde'] === null || (p['desde'] as string) <= (p['hasta'] as string)) &&
    ['kcal', 'proteinaG', 'carbohidratosG', 'grasaG', 'ayunoHoras'].every((c) => numeroONulo(p[c]))
  );
}

const TIEMPOS_COMIDA = ['desayuno', 'comida', 'cena', 'colacion'];

/** Un alimento de una comida habitual: de la base o propio, con cantidad y porción. */
function itemHabitualValido(v: unknown): boolean {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const i = v as Record<string, unknown>;
  return (
    (i['origen'] === 'base' || i['origen'] === 'propio') &&
    typeof i['alimentoId'] === 'string' &&
    esNumero(i['cantidad']) &&
    (i['cantidad'] as number) > 0 &&
    typeof i['porcion'] === 'string'
  );
}

function comidaHabitualValida(v: unknown): v is ComidaHabitual {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const h = v as Record<string, unknown>;
  return (
    typeof h['id'] === 'string' &&
    typeof h['nombre'] === 'string' &&
    TIEMPOS_COMIDA.includes(h['tiempo'] as string) &&
    Array.isArray(h['dias']) &&
    h['dias'].every((d) => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6) &&
    Array.isArray(h['items']) &&
    h['items'].every(itemHabitualValido) &&
    esFechaLocal(h['desde']) &&
    typeof h['creadoEn'] === 'string' &&
    typeof h['actualizadoEn'] === 'string'
  );
}

/** Qué acepta cada campo. Lo que no cumple se reemplaza por el predeterminado. */
const REGLAS: { readonly [K in keyof Settings]-?: (valor: unknown) => boolean } = {
  id: (v) => v === 1,
  schemaVersion: (v) => Number.isInteger(v),
  perfilId: (v) => typeof v === 'string',
  nombre: esTextoONulo,
  sexo: unoDe(['mujer', 'hombre'], true),
  fechaNacimiento: (v) => v === null || esFechaLocal(v),
  estaturaCm: (v) => v === null || (esNumero(v) && v > 0),
  estaturaCapturada: (v) =>
    v === null ||
    (typeof v === 'object' &&
      !Array.isArray(v) &&
      esNumero((v as Record<string, unknown>)['valor']) &&
      unoDe(['cm', 'in'])((v as Record<string, unknown>)['unidad'])),
  nivelActividad: unoDe(NIVELES_ACTIVIDAD, true),
  primerosPasosHechos: (v) => typeof v === 'boolean',
  unidadPeso: unoDe(['kg', 'lb']),
  unidadLongitud: unoDe(['cm', 'in']),
  tema: unoDe(['sistema', 'claro', 'oscuro']),
  descansoPredeterminadoSeg: (v) => Number.isInteger(v) && (v as number) >= 0 && (v as number) <= 3600,
  incrementoPredeterminadoKg: (v) => esNumero(v) && v > 0,
  pantallaEncendida: (v) => typeof v === 'boolean',
  avisoConPantallaBloqueada: (v) => typeof v === 'boolean',
  avisoConAppCerrada: (v) => typeof v === 'boolean',
  rutinasDe: unoDe(['janeth', 'fernando'], true),
  modalidadGym: unoDe(['gimnasio', 'casa']),
  barraYDiscos: barraYDiscosValido,
  basculaTendenciaId: esTextoONulo,
  ultimaBasculaId: esTextoONulo,
  medidasOcultas: (v) => Array.isArray(v) && v.every((m) => TIPOS_MEDIDA.includes(m as TipoMedida)),
  metodoAyuno: unoDe(['comidas', 'temporizador']),
  metaKcalManual: numeroONulo,
  metaProteinaManualG: numeroONulo,
  metaCarbohidratosManualG: numeroONulo,
  metaGrasaManualG: numeroONulo,
  metaAyunoManualHoras: numeroONulo,
  metasManualesDesde: (v) => v === null || esFechaLocal(v),
  metasManualesAnteriores: (v) => Array.isArray(v) && v.every(periodoValido),
  favoritosBase: (v) => Array.isArray(v) && v.every((id) => typeof id === 'string'),
  comidasHabituales: (v) => Array.isArray(v) && v.every(comidaHabitualValida),
  avisoEtapaVisto: esTextoONulo,
  avisoAyunoLargoVisto: esTextoONulo,
  ventanaCerradaEn: (v) => v === null || (typeof v === 'string' && !Number.isNaN(Date.parse(v))),
  ultimoRespaldoEn: (v) => v === null || (typeof v === 'string' && !Number.isNaN(Date.parse(v))),
  diasAvisoRespaldo: (v) => Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 60,
  almacenamientoPersistente: (v) => v === null || typeof v === 'boolean',
  cifradoRespaldo: cifradoValido,
  creadoEn: (v) => typeof v === 'string',
};

/** Nombre de cada campo en los avisos. */
const ETIQUETAS: Partial<Record<keyof Settings, string>> = {
  nombre: 'nombre',
  sexo: 'sexo',
  fechaNacimiento: 'fecha de nacimiento',
  estaturaCm: 'estatura',
  estaturaCapturada: 'estatura capturada',
  nivelActividad: 'nivel de actividad',
  unidadPeso: 'unidad de peso',
  unidadLongitud: 'unidad de medidas',
  tema: 'apariencia',
  avisoConPantallaBloqueada: 'aviso con la pantalla bloqueada',
  avisoConAppCerrada: 'aviso con la app cerrada',
  diasAvisoRespaldo: 'días del recordatorio',
  cifradoRespaldo: 'cifrado',
  medidasOcultas: 'medidas ocultas',
  favoritosBase: 'favoritos de la base de alimentos',
  comidasHabituales: 'comidas habituales',
  avisoEtapaVisto: 'aviso de etapa nueva',
  avisoAyunoLargoVisto: 'aviso de ayuno largo',
  ventanaCerradaEn: 'cierre de la ventana de alimentación',
  rutinasDe: 'rutinas precargadas',
  modalidadGym: 'modalidad de Gym',
  barraYDiscos: 'barra y discos',
  metaKcalManual: 'meta manual de kcal',
  metaProteinaManualG: 'meta manual de proteína',
  metaCarbohidratosManualG: 'meta manual de carbohidratos',
  metaGrasaManualG: 'meta manual de grasa',
  metaAyunoManualHoras: 'meta manual de ayuno',
  metasManualesDesde: 'fecha de las metas manuales',
  metasManualesAnteriores: 'metas manuales anteriores',
};

export interface AjustesNormalizados {
  ajustes: Settings;
  /** Campos presentes con un valor imposible, reemplazados por el predeterminado. */
  corregidos: string[];
}

/**
 * Deja un registro de ajustes leído de un archivo en una forma que la app
 * puede usar. Lo que falta (un respaldo de una versión anterior) toma el
 * predeterminado sin avisar; lo que viene con un valor imposible (un archivo
 * editado o dañado) también, pero se reporta para mostrarlo en la vista previa
 * antes de confirmar. Los datos capturados del usuario no pasan por aquí.
 */
export function normalizarAjustes(registro: Readonly<Record<string, unknown>>): AjustesNormalizados {
  const ajustes = {
    ...SETTINGS_PREDETERMINADOS,
    medidasOcultas: [],
    favoritosBase: [],
    metasManualesAnteriores: [],
  } as Settings;
  const destino = ajustes as unknown as Record<string, unknown>;
  const corregidos: string[] = [];

  for (const campo of Object.keys(REGLAS) as (keyof Settings)[]) {
    if (!(campo in registro) || registro[campo] === undefined) continue;
    const valor = registro[campo];
    if (REGLAS[campo](valor)) {
      // Las listas se copian y sin repetidos (los favoritos no se duplican).
      destino[campo] = Array.isArray(valor) ? [...new Set(valor)] : valor;
    } else {
      corregidos.push(ETIQUETAS[campo] ?? campo);
    }
  }

  return { ajustes, corregidos };
}
